
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Palette, Type, ListOrdered, Square, FileText, Image, Undo2, ZoomIn } from 'lucide-react';
import { TemplatePreview } from './TemplatePreview';
import { ColorSettings } from './ColorSettings';
import { SectionSettings } from './SectionSettings';
import { TypographySettings } from './TypographySettings';
import { SpacingSettings } from './SpacingSettings';
import { BorderStyleSettings } from './BorderStyleSettings';
import { HeaderFooterSettings } from './HeaderFooterSettings';
import { SectionStyleSettings } from './SectionStyleSettings';
import { MediaSettings } from './MediaSettings';
import { Button } from '@/components/ui/button';
import { TemplateStyle } from './templateData';
import { templateGenerators } from '@/utils/pdf-templates';
import { mockPackageData } from '@/utils/pdf-templates/mockData';
import { Column, TypographySettings as TypoType, SpacingSettings as SpacingType, BorderSettings as BorderType, HeaderSettings, FooterSettings, WatermarkSettings, SectionStyle } from '@/utils/pdf-templates/types';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Columns2 } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';

interface TemplateCardProps {
    template: { id: string; name: string; };
    settings: TemplateStyle;
    onUpdate: (newSettings: Partial<TemplateStyle>) => void;
    onReset: () => void;
}

const defaultTypo: TypoType = { titleSize: 18, textSize: 13, fontWeight: 600, lineHeight: 1.6 };
const defaultSpacing: SpacingType = { pageMarginTop: 30, pageMarginRight: 35, pageMarginBottom: 30, pageMarginLeft: 35, sectionGap: 25, sectionPadding: 20 };
const defaultBorders: BorderType = { width: 1, radius: 16, style: 'solid' };
const defaultHeader: HeaderSettings = { showCover: true, coverHeight: 400 };
const defaultFooter: FooterSettings = { showFooter: true, showQr: true, showPageNumber: true, customText: '' };
const defaultWatermark: WatermarkSettings = { text: '', opacity: 0.08, size: 60, rotation: -35 };

export function TemplateCard({ template, settings, onUpdate, onReset }: TemplateCardProps) {
    const [htmlContent, setHtmlContent] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const generatePreview = async () => {
            if (!settings) return;
            setIsLoading(true);
            try {
                const enhancedMockData = {
                    ...mockPackageData,
                    clientType: 'b2b' as const,
                    companyName: 'شركة وهمية للمعاينة',
                    companyLogoUrl: '/placeholder.svg',
                };
                const generator = templateGenerators[template.id as keyof typeof templateGenerators] || templateGenerators.template1;
                const html = await generator(enhancedMockData, settings);
                setHtmlContent(html);
            } catch (error) {
                console.error(`Failed to generate preview for ${template.id}:`, error);
                setHtmlContent('<div style="padding: 20px; text-align: center; color: red;">فشل في تحميل المعاينة</div>');
            } finally {
                setIsLoading(false);
            }
        };
        generatePreview();
    }, [settings, template.id]);
    
    if (!settings) return null;

    return (
        <Card className="overflow-hidden shadow-sm hover:shadow-lg transition-shadow duration-300">
            <CardHeader className="p-4 border-b bg-muted/30">
                <TemplatePreview htmlContent={htmlContent} isLoading={isLoading} />
                <div className="flex justify-between items-center mt-4">
                    <div>
                        <CardTitle className="text-lg">{template.name}</CardTitle>
                        <CardDescription>انقر على التبويبات للتخصيص</CardDescription>
                    </div>
                    <div className="flex items-center gap-1">
                        <Dialog>
                            <DialogTrigger asChild>
                                <Button variant="ghost" size="icon" aria-label="معاينة أكبر" className="text-muted-foreground hover:text-foreground">
                                    <ZoomIn className="h-4 w-4" />
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-4xl h-[90vh] flex flex-col">
                                <DialogHeader>
                                    <DialogTitle>معاينة حية: {template.name}</DialogTitle>
                                </DialogHeader>
                                <div className="flex-1 overflow-auto">
                                    {isLoading ? (
                                        <div className="w-full h-full flex items-center justify-center text-muted-foreground">جاري التحميل...</div>
                                    ) : (
                                        <iframe
                                            srcDoc={htmlContent}
                                            title={`Large preview for ${template.name}`}
                                            className="w-full h-full border-0 rounded-md"
                                            sandbox="allow-same-origin"
                                        />
                                    )}
                                </div>
                            </DialogContent>
                        </Dialog>
                        <Button variant="ghost" size="icon" onClick={onReset} aria-label="إعادة تعيين" className="text-muted-foreground hover:text-foreground">
                            <Undo2 className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="p-0">
                <Tabs defaultValue="colors" className="w-full">
                    <TabsList className="grid w-full grid-cols-6 rounded-none h-12">
                        <TabsTrigger value="colors" className="text-xs gap-1"><Palette className="w-3.5 h-3.5"/> الألوان</TabsTrigger>
                        <TabsTrigger value="typography" className="text-xs gap-1"><Type className="w-3.5 h-3.5"/> الخطوط</TabsTrigger>
                        <TabsTrigger value="layout" className="text-xs gap-1"><ListOrdered className="w-3.5 h-3.5"/> التخطيط</TabsTrigger>
                        <TabsTrigger value="borders" className="text-xs gap-1"><Square className="w-3.5 h-3.5"/> الحدود</TabsTrigger>
                        <TabsTrigger value="header" className="text-xs gap-1"><FileText className="w-3.5 h-3.5"/> غلاف</TabsTrigger>
                        <TabsTrigger value="media" className="text-xs gap-1"><Image className="w-3.5 h-3.5"/> وسائط</TabsTrigger>
                    </TabsList>

                    <ScrollArea className="h-[400px]">
                        <TabsContent value="colors" className="p-4 mt-0">
                            <ColorSettings
                                colors={settings.colors}
                                onColorChange={(name, value) => onUpdate({ colors: { ...settings.colors, [name]: value } })}
                            />
                        </TabsContent>

                        <TabsContent value="typography" className="p-4 mt-0">
                            <TypographySettings
                                typography={settings.typography || defaultTypo}
                                onChange={(v) => onUpdate({ typography: v })}
                            />
                        </TabsContent>

                        <TabsContent value="layout" className="p-4 mt-0">
                            <div className="mb-4 pb-4 border-b">
                                <label className="text-sm font-medium flex items-center gap-2">
                                    <Columns2 className="w-4 h-4" />
                                    عدد الأعمدة
                                </label>
                                <p className="text-xs text-muted-foreground mt-1 mb-3">اختر عدد الأعمدة لعرضها.</p>
                                <div className="flex items-center gap-2">
                                    {[1, 2, 3].map(num => (
                                        <Button
                                            key={num}
                                            variant={settings.layoutGridCols === num ? 'default' : 'outline'}
                                            size="sm"
                                            onClick={() => onUpdate({ layoutGridCols: num })}
                                            className="w-12"
                                        >
                                            {num}
                                        </Button>
                                    ))}
                                </div>
                            </div>
                            <div className="mb-4 pb-4 border-b">
                                <h4 className="font-semibold text-sm mb-3">التباعد والهوامش</h4>
                                <SpacingSettings
                                    spacing={settings.spacing || defaultSpacing}
                                    onChange={(v) => onUpdate({ spacing: v })}
                                />
                            </div>
                            <SectionSettings
                                columns={settings.columns}
                                onColumnsChange={(cols) => onUpdate({ columns: cols })}
                                gridCols={settings.layoutGridCols || 1}
                            />
                        </TabsContent>

                        <TabsContent value="borders" className="p-4 mt-0">
                            <BorderStyleSettings
                                borders={settings.borders || defaultBorders}
                                onChange={(v) => onUpdate({ borders: v })}
                            />
                        </TabsContent>

                        <TabsContent value="header" className="p-4 mt-0">
                            <HeaderFooterSettings
                                header={settings.header || defaultHeader}
                                footer={settings.footer || defaultFooter}
                                watermark={settings.watermark || defaultWatermark}
                                onHeaderChange={(v) => onUpdate({ header: v })}
                                onFooterChange={(v) => onUpdate({ footer: v })}
                                onWatermarkChange={(v) => onUpdate({ watermark: v })}
                            />
                        </TabsContent>

                        <TabsContent value="media" className="p-4 mt-0">
                            <MediaSettings
                                media={settings.media || {}}
                                onMediaChange={(type, value) => onUpdate({ media: { ...settings.media, [type]: value } })}
                            />
                            <div className="mt-6 border-t pt-4">
                                <h4 className="font-semibold text-sm mb-3">تخصيص الأقسام الفردية</h4>
                                <SectionStyleSettings
                                    sectionStyles={settings.sectionStyles || {}}
                                    onChange={(v) => onUpdate({ sectionStyles: v })}
                                />
                            </div>
                        </TabsContent>
                    </ScrollArea>
                </Tabs>
            </CardContent>
        </Card>
    );
}
